import React, { useState } from 'react';
import { Alert, Button, Form, Modal, Spinner } from 'react-bootstrap';
import { MdEdit, MdSave, MdAssessment, MdDownload, MdDateRange, MdInfoOutline, MdCheckCircle, MdClose } from 'react-icons/md';
import './EditResultDisplay.css';
import moment from 'moment';
import Request from '../../Request';
import PermissionGate from '../ReusableComponents/PermissionGate';

const enabled = value => value === true || value === 1 || value === '1' || value === 'true';
const choices = [
  ['folha_respostas', 'Folha de resposta do candidato'],
  ['relacao_acerto_erros', 'Relação de acertos/erros'],
  ['folha_relacao', 'Folha de resposta com relação de acertos/erros'],
  ['caderno_gabarito', 'Caderno de questões com gabarito'],
  ['caderno_respostas', 'Caderno com gabarito e respostas do candidato'],
  ['none', 'Nenhum conteúdo'],
];

export default function EditResultDisplay({ prova, onUpdated }) {
  const [show, setShow] = useState(false);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState('');
  const [values, setValues] = useState({});
  const [success, setSuccess] = useState(false);
  const open = () => {
    const exibicao = enabled(prova.mirror) ? (enabled(prova.show_acertos) ? 'caderno_respostas' : 'caderno_gabarito')
      : enabled(prova.folha_resposta) ? (enabled(prova.show_acertos) ? 'folha_relacao' : 'folha_respostas')
        : enabled(prova.show_acertos) ? 'relacao_acerto_erros' : 'none';
    setValues({ exibicao, allow_result_download: enabled(prova.allow_result_download),
      result_release_starts_at: prova.result_release_starts_at ? moment(prova.result_release_starts_at).format('YYYY-MM-DDTHH:mm') : '',
      result_release_ends_at: prova.result_release_ends_at ? moment(prova.result_release_ends_at).format('YYYY-MM-DDTHH:mm') : '' });
    setError(''); setSuccess(false); setShow(true);
  };
  const change = (key, value) => setValues(previous => ({ ...previous, [key]: value }));
  const save = async event => {
    event.preventDefault();
    if (saving) return;
    const start = values.result_release_starts_at;
    const end = values.result_release_ends_at;
    if (!!start !== !!end || (start && !moment(start).isBefore(moment(end)))) {
      setError('Informe as duas datas, com o fim da liberação posterior ao início.'); return;
    }
    setSaving(true); setError('');
    try {
      const response = await Request.Post(`faculdade/update_prova_result_display/${prova.id}`, JSON.stringify({
        ...values,
        allow_result_download: values.exibicao !== 'none' && values.allow_result_download,
        result_release_starts_at: start || null,
        result_release_ends_at: end || null,
      }), { 'Content-Type': 'application/json' });
      if (!response?.status || !response.prova) {
        setError(response?.errors ? Object.values(response.errors).flat().join('\n') : response?.message || 'Não foi possível salvar. Tente novamente.');
        return;
      }
      onUpdated(response.prova);
      setShow(false); setSuccess(true);
    } catch (err) {
      setError('Não foi possível salvar. Verifique sua conexão e tente novamente.');
    } finally { setSaving(false); }
  };
  return <>
    <PermissionGate module="provas" action="edit">
      <Button variant="outline-primary" size="sm" className="prova-result-edit hidethis" onClick={open}><MdEdit aria-hidden="true" />Editar exibição</Button>
    </PermissionGate>
    {success && <div className="prova-result-success hidethis" role="status">
      <MdCheckCircle aria-hidden="true" /><span>Exibição atualizada.</span>
      <button type="button" aria-label="Dispensar confirmação" onClick={() => setSuccess(false)}><MdClose aria-hidden="true" /></button>
    </div>}
    <Modal className="prova-result-modal hidethis" show={show} onHide={() => !saving && setShow(false)} centered size="lg" backdrop={saving ? 'static' : true} keyboard={!saving}>
      <Modal.Header closeButton={!saving}>
        <div className="prova-result-modal-heading">
          <span className="prova-result-modal-icon"><MdAssessment aria-hidden="true" /></span>
          <div><span className="prova-result-modal-kicker">Resultados da prova</span><Modal.Title>Exibição ao finalizar</Modal.Title><p>Defina o que o candidato poderá consultar após concluir a prova.</p></div>
        </div>
      </Modal.Header>
      <Form onSubmit={save}>
        <Modal.Body>
          {error && <Alert variant="danger" style={{ whiteSpace: 'pre-line' }}>{error}</Alert>}
          <section className="prova-result-section">
            <div className="prova-result-section-heading"><span className="prova-result-section-icon is-purple"><MdAssessment aria-hidden="true" /></span><div><h3>Conteúdo disponível</h3><p>Escolha como as respostas e o gabarito serão apresentados.</p></div></div>
          <Form.Group controlId="prova-result-content" className="mb-0">
            <Form.Label>Conteúdo exibido</Form.Label>
            <Form.Control as="select" value={values.exibicao || 'none'} disabled={saving} onChange={event => {
              const exibicao = event.target.value;
              setValues(previous => ({ ...previous, exibicao, allow_result_download: exibicao === 'none' ? false : previous.allow_result_download }));
            }}>
              {choices.map(([value, label]) => <option key={value} value={value} disabled={prova.type === 'dissertativa' && value !== 'none'}>{label}</option>)}
            </Form.Control>
          </Form.Group>
          </section>
          {values.exibicao !== 'none' && <section className="prova-result-section prova-result-download-section">
            <div className="prova-result-section-heading"><span className="prova-result-section-icon is-green"><MdDownload aria-hidden="true" /></span><div><h3>Download do conteúdo</h3><p>Permita que o candidato salve o conteúdo liberado.</p></div></div>
            <Form.Group controlId="prova-result-download" className="mb-0">
            <Form.Check type="switch" label="Permitir download do conteúdo" checked={!!values.allow_result_download} disabled={saving} onChange={event => change('allow_result_download', event.target.checked)} />
          </Form.Group></section>}
          <section className="prova-result-section">
            <div className="prova-result-section-heading"><span className="prova-result-section-icon is-blue"><MdDateRange aria-hidden="true" /></span><div><h3>Período de liberação</h3><p>Escolha quando o conteúdo ficará disponível para consulta.</p></div></div>
          <div className="prova-result-dates">
            <Form.Group controlId="prova-result-start"><Form.Label>Disponível de</Form.Label><Form.Control type="datetime-local" value={values.result_release_starts_at || ''} disabled={saving} onChange={event => change('result_release_starts_at', event.target.value)} /></Form.Group>
            <Form.Group controlId="prova-result-end"><Form.Label>Disponível até</Form.Label><Form.Control type="datetime-local" value={values.result_release_ends_at || ''} disabled={saving} onChange={event => change('result_release_ends_at', event.target.value)} /></Form.Group>
          </div>
          <div className="prova-result-hint"><MdInfoOutline aria-hidden="true" /><span>Deixe as duas datas vazias para liberar assim que finalizar, sem data limite.</span></div>
          </section>
        </Modal.Body>
        <Modal.Footer className="prova-result-modal-footer">
          <Button variant="outline-secondary" disabled={saving} onClick={() => setShow(false)}>Cancelar</Button>
          <Button variant="primary" type="submit" disabled={saving}>{saving ? <><Spinner animation="border" size="sm" className="mr-2" />Salvando...</> : <><MdSave className="mr-2" aria-hidden="true" />Salvar exibição</>}</Button>
        </Modal.Footer>
      </Form>
    </Modal>
  </>;
}
